import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { PRICING, COMPARISON_ROWS } from '../../config/pricingData';

const PricingComparison = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [isAnnual, setIsAnnual] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [checkoutError, setCheckoutError] = useState(null);
  const tableScrollRef = useRef(null);

  // Auto-scroll table to the right on mobile so Grandmaster is visible
  useEffect(() => {
    const el = tableScrollRef.current;
    if (el && el.scrollWidth > el.clientWidth) {
      el.scrollLeft = el.scrollWidth - el.clientWidth;
    }
  }, []);

  const handleGetStarted = () => {
    if (currentUser) {
      navigate('/me');
    } else {
      navigate(`/auth?mode=signup&redirect=${encodeURIComponent('/onboarding')}`);
    }
  };

  const handleCheckout = (tier) => {
    const checkoutTier = tier === 'grandmaster' ? 'grandmaster' : 'master';
    const billing = isAnnual ? 'annual' : 'monthly';
    if (!currentUser) {
      navigate(`/auth?mode=signup&redirect=${encodeURIComponent(`/onboarding?checkout=${checkoutTier}&billing=${billing}`)}`);
      return;
    }
    navigate(`/onboarding?checkout=${checkoutTier}&billing=${billing}`);
  };

  // Pricing from shared config
  const masterMonthly = PRICING.master.monthly;
  const grandmasterMonthly = PRICING.grandmaster.monthly;
  const masterAnnual = PRICING.master.annual;
  const grandmasterAnnual = PRICING.grandmaster.annual;

  return (
    <section id="pricing" className="py-12 md:py-24 bg-[#151515]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-5xl font-bold mb-4">Built for chess. Priced to grow.</h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Everything you need to build, launch, and monetize your chess brand.
          </p>

          {/* Annual Toggle */}
          <div className="mt-8 flex items-center justify-center gap-4">
            <span className={`text-sm font-medium ${!isAnnual ? 'text-white' : 'text-gray-500'}`}>Monthly</span>
            <button
              onClick={() => setIsAnnual(!isAnnual)}
              className={`relative w-14 h-7 rounded-full transition-colors ${isAnnual ? 'bg-chess-green' : 'bg-white/20'}`}
            >
              <div className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${isAnnual ? 'translate-x-8' : 'translate-x-1'}`} />
            </button>
            <span className={`text-sm font-medium ${isAnnual ? 'text-white' : 'text-gray-500'}`}>
              Annual
              <span className="ml-2 px-2 py-0.5 bg-chess-green/20 text-chess-green text-xs rounded-full">2 Months Free!</span>
            </span>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-chess-green/10 border border-chess-green/30">
            <span className="flex items-center gap-1.5 text-sm font-semibold text-chess-green">
              <i className="fas fa-check-circle"></i> 14-Day Free Trial
            </span>
            <span className="w-px h-4 bg-chess-green/30"></span>
            <span className="flex items-center gap-1.5 text-sm text-gray-300">
              <i className="fas fa-rotate-left text-gray-400"></i> Cancel Anytime
            </span>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-16">

          {/* Master Plan */}
          <div className="p-6 rounded-2xl bg-gradient-to-b from-chess-green/20 to-white/5 border border-chess-green/50 flex flex-col text-center relative shadow-2xl">
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-chess-green text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
              MOST POPULAR
            </div>
            <h3 className="text-2xl font-bold text-chess-green mb-2">Master</h3>
            <div className="text-4xl font-bold text-white mb-1">
              ${isAnnual ? masterAnnual : masterMonthly}
              <span className="text-lg text-gray-500 font-normal">/{isAnnual ? 'year' : 'mo'}</span>
            </div>
            {isAnnual && (
              <p className="text-chess-green text-xs mb-2">
                Save ${(masterMonthly * 12 - masterAnnual).toFixed(0)}/year
              </p>
            )}
            <p className="text-gray-400 text-sm mb-6">Monetize your passion for chess.</p>
            <p className="text-white text-sm mb-6 flex-1">
              <span className="font-semibold">10% platform fee</span>
              <span className="text-gray-400"> on transactions</span>
            </p>
            <button
              onClick={() => handleCheckout('master')}
              disabled={checkoutLoading === 'master'}
              className="w-full py-3 rounded-xl bg-chess-green text-white font-bold hover:bg-opacity-90 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-sm"
            >
              {checkoutLoading === 'master' ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Processing...
                </>
              ) : (
                <>
                  Get Master — ${isAnnual ? masterAnnual : masterMonthly}/{isAnnual ? 'yr' : 'mo'}
                  <i className="fas fa-arrow-right ml-2"></i>
                </>
              )}
            </button>
          </div>

          {/* Grandmaster Plan */}
          <div className="p-6 rounded-2xl bg-gradient-to-b from-yellow-500/20 to-white/5 border border-yellow-500/50 flex flex-col text-center relative shadow-2xl">
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-yellow-500 text-gray-900 px-3 py-1 rounded-full text-xs font-bold shadow-lg">
              BEST VALUE
            </div>
            <h3 className="text-2xl font-bold text-yellow-400 mb-2">Grandmaster</h3>
            <div className="text-4xl font-bold text-white mb-1">
              ${isAnnual ? grandmasterAnnual : grandmasterMonthly}
              <span className="text-lg text-gray-500 font-normal">/{isAnnual ? 'year' : 'mo'}</span>
            </div>
            {isAnnual && (
              <p className="text-yellow-400 text-xs mb-2">
                Save ${(grandmasterMonthly * 12 - grandmasterAnnual).toFixed(0)}/year
              </p>
            )}
            <p className="text-gray-400 text-sm mb-6">Keep more of what you earn.</p>
            <p className="text-white text-sm mb-6 flex-1">
              <span className="font-semibold">5% platform fee</span>
            </p>
            <button
              onClick={() => handleCheckout('grandmaster')}
              disabled={checkoutLoading === 'grandmaster'}
              className="w-full py-3 rounded-xl bg-yellow-500 text-gray-900 font-bold hover:bg-yellow-400 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-sm"
            >
              {checkoutLoading === 'grandmaster' ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Processing...
                </>
              ) : (
                <>
                  Get Grandmaster — ${isAnnual ? grandmasterAnnual : grandmasterMonthly}/{isAnnual ? 'yr' : 'mo'}
                  <i className="fas fa-arrow-right ml-2"></i>
                </>
              )}
            </button>
          </div>
        </div>

        {/* ROI Callout */}
        <div className="max-w-3xl mx-auto mb-20 text-center">
          <div className="inline-flex items-center gap-3 px-6 py-4 rounded-2xl bg-white/5 border border-white/10">
            <i className="fas fa-calculator text-chess-green text-xl"></i>
            <p className="text-gray-300 text-sm">
              <strong className="text-white">Doing $400+/mo in coaching or shop sales?</strong>
              <span className="text-gray-400 ml-2">Grandmaster pays for itself in saved fees.</span>
            </p>
          </div>
        </div>

        {/* Comparison Table */}
        <div className="max-w-5xl mx-auto mb-24 mt-20">
          <h3 className="text-xl font-bold text-center text-white mb-6">Compare Plans</h3>
          <div ref={tableScrollRef} className="overflow-x-auto rounded-xl border border-white/10 bg-white/[0.02]">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-white/10 bg-white/5">
                  <th className="py-4 px-4 text-gray-400 font-medium text-sm sticky left-0 bg-[#1a1a1a] z-10 min-w-[120px]">Feature</th>
                  <th className="py-4 px-4 text-center text-chess-green font-bold text-base">Master</th>
                  <th className="py-4 px-4 text-center text-yellow-400 font-bold text-base">Grandmaster</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {COMPARISON_ROWS.map((row, idx) => (
                  <tr key={idx} className="border-b border-white/5">
                    <td className="py-3 px-4 text-gray-300 sticky left-0 bg-[#151515] z-10">{row.feature}</td>
                    <td className="py-3 px-4 text-center">
                      {row.master === true ? <i className="fas fa-check text-chess-green"></i>
                        : row.master === false || row.master == null ? <span className="text-gray-600">&mdash;</span>
                        : <span className={`text-xs ${row.master === 'Unlimited' ? 'text-chess-green font-medium' : 'text-white font-medium'}`}>{row.master}</span>}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {row.grandmaster === 'double-check' ? <><i className="fas fa-check text-yellow-400 mr-1"></i><i className="fas fa-check text-yellow-400"></i></>
                        : row.grandmaster === true ? <i className="fas fa-check text-yellow-400"></i>
                        : row.grandmaster === false || row.grandmaster == null ? <span className="text-gray-600">&mdash;</span>
                        : <span className={`text-xs ${row.grandmaster === '5%' || row.grandmaster === 'Unlimited' || row.grandmaster.includes('.com') ? 'text-yellow-400 font-bold' : 'text-yellow-400 font-medium'}`}>{row.grandmaster}</span>}
                    </td>
                  </tr>
                ))}
                <tr className="border-b border-white/5 bg-white/[0.02]">
                  <td className="py-4 px-4 text-gray-400 font-medium sticky left-0 bg-[#161616] z-10">Price</td>
                  <td className="py-4 px-4 text-center">
                    <span className="text-xl font-bold text-chess-green">${isAnnual ? masterAnnual : masterMonthly}</span>
                    <span className="text-gray-500 text-sm">/{isAnnual ? 'yr' : 'mo'}</span>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <span className="text-xl font-bold text-yellow-400">${isAnnual ? grandmasterAnnual : grandmasterMonthly}</span>
                    <span className="text-gray-500 text-sm">/{isAnnual ? 'yr' : 'mo'}</span>
                  </td>
                </tr>
                <tr className="bg-white/[0.02]">
                  <td className="py-5 px-4 sticky left-0 bg-[#161616] z-10"></td>
                  <td className="py-5 px-4 text-center">
                    <button
                      onClick={() => handleCheckout('master')}
                      disabled={checkoutLoading === 'master'}
                      className="px-4 py-2 rounded-lg bg-chess-green text-white text-sm font-bold hover:bg-opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {checkoutLoading === 'master' ? (
                        <><i className="fas fa-spinner fa-spin mr-1"></i> Processing</>
                      ) : (
                        <>Get Master</>
                      )}
                    </button>
                  </td>
                  <td className="py-5 px-4 text-center">
                    <button
                      onClick={() => handleCheckout('grandmaster')}
                      disabled={checkoutLoading === 'grandmaster'}
                      className="px-4 py-2 rounded-lg bg-yellow-500 text-gray-900 text-sm font-bold hover:bg-yellow-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {checkoutLoading === 'grandmaster' ? (
                        <><i className="fas fa-spinner fa-spin mr-1"></i> Processing</>
                      ) : (
                        <>Get Grandmaster</>
                      )}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="text-center space-y-1 mt-4">
            <p className="text-gray-500 text-xs">
              <i className="fab fa-stripe text-gray-600 mr-1"></i>
              Standard Stripe processing fees apply to all transactions (2.9% + $0.30)
            </p>
            <p className="text-gray-500 text-xs">
              <i className="fab fa-paypal text-gray-600 mr-1"></i>
              Accept payments via PayPal — coming soon
            </p>
          </div>
        </div>

        {/* Done For You Section - LAST */}
        <div id="done-for-you" style={{ scrollMarginTop: '9rem' }} className="max-w-4xl mx-auto mb-12">
          <div className="relative rounded-2xl p-[1px] bg-gradient-to-br from-yellow-500/60 via-yellow-600/40 to-yellow-500/60 overflow-hidden">
            <div className="rounded-2xl bg-[#0d0d0d] p-8 md:p-12">
              {/* Header */}
              <div className="text-center mb-10">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-yellow-400 to-yellow-600 mb-4 shadow-lg shadow-yellow-500/20">
                  <i className="fas fa-chess-queen text-gray-900 text-2xl"></i>
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-white mb-3">Done-For-You Setup</h3>
                <p className="text-gray-400 text-lg">
                  Share your vision. We'll design and launch your complete chess website.
                </p>
              </div>

              {/* Features Grid */}
              <div className="grid sm:grid-cols-2 gap-4 mb-8 max-w-2xl mx-auto">
                <div className="flex items-center gap-3 p-4 rounded-xl bg-yellow-500/[0.05] border border-yellow-500/10">
                  <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-palette text-yellow-400"></i>
                  </div>
                  <span className="text-gray-200 text-sm">Premium customized design</span>
                </div>
                <div className="flex items-center gap-3 p-4 rounded-xl bg-yellow-500/[0.05] border border-yellow-500/10">
                  <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-arrows-turn-to-dots text-yellow-400"></i>
                  </div>
                  <span className="text-gray-200 text-sm">Content migration & setup</span>
                </div>
                <div className="flex items-center gap-3 p-4 rounded-xl bg-yellow-500/[0.05] border border-yellow-500/10">
                  <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-puzzle-piece text-yellow-400"></i>
                  </div>
                  <span className="text-gray-200 text-sm">Academy, shop & library configured</span>
                </div>
                <div className="flex items-center gap-3 p-4 rounded-xl bg-yellow-500/[0.05] border border-yellow-500/10">
                  <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-video text-yellow-400"></i>
                  </div>
                  <span className="text-gray-200 text-sm">1-on-1 onboarding with founder</span>
                </div>
              </div>

              {/* CTA Section */}
              <div className="text-center">
                <div className="inline-flex flex-col items-center gap-4 p-6 rounded-2xl bg-gradient-to-b from-yellow-500/[0.08] to-transparent border border-yellow-500/20">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-400">Includes</span>
                    <span className="px-3 py-1 rounded-full bg-yellow-500/20 text-yellow-400 font-semibold">12-Month Subscription</span>
                  </div>
                  <a
                    href="https://cal.com/indiechess/consult"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-yellow-500 to-yellow-600 text-gray-900 font-bold rounded-xl hover:shadow-lg hover:shadow-yellow-500/30 transition-all duration-300 text-lg"
                  >
                    <i className="fas fa-calendar-check"></i>
                    Book a Free Consultation
                    <i className="fas fa-arrow-right text-sm opacity-70 group-hover:translate-x-1 transition-transform"></i>
                  </a>
                  <p className="text-gray-500 text-xs">No commitment required</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {checkoutError && (
          <div className="mt-6 bg-red-500/10 border border-red-500/20 rounded-lg p-4 max-w-3xl mx-auto text-sm text-red-400 flex items-center gap-2">
            <i className="fas fa-exclamation-triangle"></i>
            {checkoutError}
          </div>
        )}
      </div>
    </section>
  );
};

export default PricingComparison;
